import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Chip, CircularProgress, Tooltip,
  IconButton, Table, TableBody, TableCell, TableHead, TableRow, Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import BuildIcon from '@mui/icons-material/Build';
import RefreshIcon from '@mui/icons-material/Refresh';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import SEO from '../../components/SEO/SEO';
import { useRegion } from '../../context/RegionContext';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  operational:       { label: 'Operational',       color: '#22c55e', bg: '#f0fdf4', border: '#86efac', textColor: '#15803d', Icon: CheckCircleIcon },
  'performance':     { label: 'Performance Issues', color: '#f59e0b', bg: '#fffbeb', border: '#fcd34d', textColor: '#92400e', Icon: WarningAmberIcon },
  degraded:          { label: 'Partial Outage',     color: '#f97316', bg: '#fff7ed', border: '#fdba74', textColor: '#9a3412', Icon: PauseCircleIcon },
  outage:            { label: 'Major Outage',       color: '#ef4444', bg: '#fef2f2', border: '#fca5a5', textColor: '#991b1b', Icon: ErrorIcon },
  maintenance:       { label: 'Maintenance',        color: '#3b82f6', bg: '#eff6ff', border: '#93c5fd', textColor: '#1d4ed8', Icon: BuildIcon },
};

const OVERALL_LABELS = {
  operational: { emoji: '🟢', text: 'All Systems Operational' },
  degraded:    { emoji: '🟡', text: 'Partial Service Degradation' },
  outage:      { emoji: '🔴', text: 'Service Disruption' },
};

const SERVICE_META = {
  'API':                { icon: '⚡', description: 'Core REST API & request routing',   uptime: 99.98 },
  'Authentication':     { icon: '🔐', description: 'Login, JWT & session management',   uptime: 99.99 },
  'Database':           { icon: '🗄️', description: 'PostgreSQL primary data layer',     uptime: 99.95 },
  'Storage':            { icon: '📦', description: 'File & document storage',           uptime: 99.90 },
  'Email':              { icon: '✉️', description: 'Transactional email delivery',      uptime: 99.85 },
  'Background Workers': { icon: '⚙️', description: 'Posting engine & async jobs',      uptime: 99.97 },
};

// Incident messages shown when a service is degraded/outage
const INCIDENT_MESSAGES = {
  'Email': {
    title: 'Email delivery delays',
    body: 'Password reset emails and automated notifications may be delayed. Customer data is unaffected. Engineering team is investigating.',
  },
  'Database': {
    title: 'Database performance degradation',
    body: 'Some queries may be slower than usual. No data loss has occurred. Engineering team is actively investigating.',
  },
  'Storage': {
    title: 'File storage issues',
    body: 'File uploads and downloads may be intermittently slow or unavailable. Engineering team is investigating.',
  },
  'Background Workers': {
    title: 'Background job delays',
    body: 'Posting and async jobs are processing slower than usual. No data loss. Engineering team is investigating.',
  },
};

const HISTORICAL_INCIDENTS = [
  { date: '18 Jul', title: 'Email delivery delays',       status: 'Resolved' },
  { date: '12 Jul', title: 'Background workers latency',  status: 'Resolved' },
  { date: '3 Jul',  title: 'API latency spike',           status: 'Resolved' },
];

const MAINTENANCE = [
  { date: '20 Jul 2026', window: '02:00–03:00 UTC', title: 'Database optimisation', impact: 'No downtime expected' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.operational;
  const { Icon } = cfg;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: '14px !important', color: `${cfg.color} !important` }} />}
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, bgcolor: cfg.bg, color: cfg.textColor, border: `1px solid ${cfg.border}`, minWidth: 150 }}
    />
  );
};

const LatencyBar = ({ ms, maxMs = 200 }) => {
  const pct = Math.min((ms / maxMs) * 100, 100);
  const color = ms < 100 ? '#22c55e' : ms < 500 ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 80, height: 6, bgcolor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </Box>
      <Typography variant="caption" fontWeight={700} sx={{ color, minWidth: 40 }}>{ms} ms</Typography>
    </Box>
  );
};

const UptimeDots = ({ pct }) => {
  const color = pct >= 99.9 ? '#22c55e' : pct >= 99 ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 60, height: 6, bgcolor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
      </Box>
      <Typography variant="caption" fontWeight={600} sx={{ color: 'text.secondary', minWidth: 44 }}>{pct}%</Typography>
    </Box>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const StatusPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { contact } = useRegion();

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/status', { signal: AbortSignal.timeout(15000) });
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date());
    } catch {
      setData({ status: 'outage', services: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const overall = data?.status || 'operational';
  const overallCfg = STATUS_CONFIG[overall] || STATUS_CONFIG.operational;
  const overallLabel = OVERALL_LABELS[overall] || OVERALL_LABELS.operational;
  const { Icon: OverallIcon } = overallCfg;

  const degradedServices = (data?.services || []).filter(s => s.status !== 'operational');
  const incidentService = degradedServices.find(s => INCIDENT_MESSAGES[s.name]);

  const utcTimestamp = lastUpdated
    ? lastUpdated.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) +
      ' • ' +
      lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) + ' UTC'
    : null;

  const cardSx = {
    bgcolor: 'background.paper',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    mb: 3,
  };

  const sectionHeaderSx = {
    px: 3, py: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
  };

  return (
    <WebsiteLayout>
      <SEO
        title="System Status — FluxERP Uptime & Service Health"
        description="Real-time status and uptime monitoring for FluxERP services including API, authentication, database, storage, email, and background workers."
        canonical="/status"
      />

      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : '#f8fafc', minHeight: '80vh', py: 8 }}>
        <Container maxWidth="lg">

          {/* ── Header ── */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4 }}>
            <Box>
              <Typography variant="h3" fontWeight={800} gutterBottom>FluxERP Status</Typography>

              {/* Overall status line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                {loading
                  ? <CircularProgress size={22} />
                  : <OverallIcon sx={{ color: overallCfg.color, fontSize: 26 }} />
                }
                <Typography variant="h6" fontWeight={700} sx={{ color: overallCfg.textColor }}>
                  {loading ? 'Checking systems…' : `Current Status: ${overallLabel.emoji} ${overallLabel.text}`}
                </Typography>
              </Box>

              {/* Meta row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>Last Updated</Typography>
                  <Typography variant="body2" fontWeight={600}>{utcTimestamp || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>Monitoring</Typography>
                  <Chip label="Active" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, height: 22 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>Refresh</Typography>
                  <Typography variant="body2" fontWeight={600}>Every 60 seconds</Typography>
                </Box>
              </Box>
            </Box>

            <Tooltip title="Refresh now">
              <IconButton onClick={fetchStatus} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider', mt: 1 }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* ── Incident Banner ── */}
          {!loading && incidentService && INCIDENT_MESSAGES[incidentService.name] && (() => {
            const inc = INCIDENT_MESSAGES[incidentService.name];
            return (
              <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 3, p: 3, mb: 3 }}>
                <Typography fontWeight={800} sx={{ color: '#92400e', mb: 0.5 }}>⚠️ Current Incident</Typography>
                <Typography fontWeight={700} sx={{ color: '#92400e', mb: 0.5 }}>{inc.title}</Typography>
                <Typography variant="body2" sx={{ color: '#78350f' }}>{inc.body}</Typography>
              </Box>
            );
          })()}

          {/* ── Service Table ── */}
          <Box sx={cardSx}>
            <Box sx={sectionHeaderSx}>
              <Typography fontWeight={700} variant="subtitle1">Service Health</Typography>
            </Box>
            <Paper elevation={0} sx={{ overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa' }}>
                    {['Service', 'Status', 'Response Time', 'Uptime (30d)'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !data
                    ? [...Array(6)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                              <CircularProgress size={14} />
                              <Typography variant="body2" color="text.secondary">Checking…</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    : (data?.services || []).map(s => {
                        const meta = SERVICE_META[s.name] || { icon: '🔧', description: '', uptime: 99.9 };
                        return (
                          <TableRow key={s.name} hover sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography fontSize={20}>{meta.icon}</Typography>
                                <Box>
                                  <Typography fontWeight={700} variant="body2">{s.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{meta.description}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell><StatusChip status={s.status} /></TableCell>
                            <TableCell><LatencyBar ms={s.latency} /></TableCell>
                            <TableCell><UptimeDots pct={meta.uptime} /></TableCell>
                          </TableRow>
                        );
                      })
                  }
                </TableBody>
              </Table>
            </Paper>
          </Box>

          {/* ── Bottom grid: Incidents + Maintenance ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>

            {/* Historical Incidents */}
            <Box sx={cardSx}>
              <Box sx={sectionHeaderSx}>
                <Typography fontWeight={700} variant="subtitle1">Recent Incidents</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {HISTORICAL_INCIDENTS.map((inc, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: i < HISTORICAL_INCIDENTS.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, fontWeight: 600 }}>{inc.date}</Typography>
                      <Typography variant="body2" fontWeight={600}>{inc.title}</Typography>
                    </Box>
                    <Chip label={inc.status} size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Scheduled Maintenance */}
            <Box sx={cardSx}>
              <Box sx={sectionHeaderSx}>
                <Typography fontWeight={700} variant="subtitle1">Scheduled Maintenance</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {MAINTENANCE.map((m, i) => (
                  <Box key={i} sx={{ py: 1.5, borderBottom: i < MAINTENANCE.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}>{m.date}</Typography>
                      <Chip label={m.window} size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">{m.title}</Typography>
                    <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600 }}>Expected impact: {m.impact}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* ── Footer ── */}
          <Box sx={{ textAlign: 'center', pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status updates every 60 seconds. Need help?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mt: 1 }}>
              <Typography variant="body2">
                <a href={`mailto:${contact?.email || 'support@fluxerp.co.uk'}`} style={{ color: 'inherit', fontWeight: 600 }}>
                  {contact?.email || 'support@fluxerp.co.uk'}
                </a>
              </Typography>
              {contact?.phone && (
                <Typography variant="body2">
                  <a href={`tel:${String(contact.phone).replace(/\s/g, '')}`} style={{ color: 'inherit', fontWeight: 600 }}>
                    {contact.phone}
                  </a>
                </Typography>
              )}
            </Box>
          </Box>

        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default StatusPage;
